/** Left navigation rail — nav links, active baskets list, sync/footer status. Mirrors design/Drabina.dc.html. */

import { useNavItems } from "@client/components/layout/navItems";
import { basketsQuery, overviewQuery, syncQuery, tokensQuery } from "@client/lib/queries";
import { basketStatus, calcBasket } from "@shared/basket";
import { ago, fmt } from "@shared/format";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export function Sidebar() {
  const navItems = useNavItems();
  const tokensQ = useQuery(tokensQuery());
  const basketsQ = useQuery(basketsQuery());
  const overviewQ = useQuery(overviewQuery());
  const syncQ = useQuery(syncQuery());

  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setTick(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);

  const tokens = tokensQ.data ?? [];
  const baskets = basketsQ.data ?? [];
  const openBaskets = baskets.filter((b) => b.status !== "closed");

  const tokensById = new Map(tokens.map((t) => [t.id, t]));
  const sideBaskets = [...openBaskets].sort((a, z) => {
    if (a.status === z.status) return 0;
    return a.status === "active" ? -1 : z.status === "active" ? 1 : 0;
  });

  const fx = overviewQ.data?.fx ?? null;
  const rateLabel = fx ? `${fmt(fx.rate, 2)} PLN/USD` : "—";
  const pricesSync = syncQ.data?.find((s) => s.key === "prices") ?? null;
  const refreshedLabel = ago(pricesSync?.lastSuccessAt ?? null, new Date(tick));

  return (
    <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col gap-0.5 border-r border-line bg-surface p-3 md:flex">
      <div className="flex items-center gap-2.5 px-2 pb-4 pt-2">
        <img src="/icon.svg" width={30} height={30} alt="" className="shrink-0" />
        <div>
          <div className="text-base font-bold leading-tight tracking-tight text-fg-1">Drabina</div>
          <div className="text-[11px] text-fg-3">prywatne · 1 użytkownik</div>
        </div>
      </div>

      <nav className="flex flex-col gap-0.5">
        {navItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            activeOptions={{ exact: item.exact ?? false }}
            className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-fg-2 no-underline transition-colors duration-100 ease-out hover:bg-line hover:text-fg-1 hover:no-underline"
            activeProps={{ className: "bg-line text-fg-1" }}
          >
            <svg
              width={18}
              height={18}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
            <span className="flex-1">{item.label}</span>
            {item.badge != null && (
              <span
                className="tabular rounded-full px-1.5 py-px text-[11px] font-semibold"
                style={{
                  background: item.badgeRed ? "rgba(240,82,82,.2)" : "var(--color-app)",
                  color: item.badgeRed ? "var(--color-danger)" : "var(--color-fg-3)",
                }}
              >
                {item.badge}
              </span>
            )}
          </Link>
        ))}
      </nav>

      <div className="px-3 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-wide text-fg-3">
        Koszyki
      </div>
      <div className="flex flex-col gap-0.5">
        {sideBaskets.map((basket) => {
          const token = tokensById.get(basket.tokenId);
          const result = calcBasket(basket);
          const status = basketStatus(basket, token?.priceUsd ?? null);
          return (
            <Link
              key={basket.id}
              to="/koszyki/$basketId"
              params={{ basketId: basket.id }}
              className="flex items-center gap-2.5 rounded-md py-2 pl-3 pr-2.5 no-underline transition-colors duration-100 ease-out hover:bg-line hover:no-underline"
              activeProps={{ className: "bg-line" }}
              style={{ borderLeft: `3px solid ${status.color}` }}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 text-[13px] font-semibold text-fg-1">
                  <span>{token?.symbol ?? "?"}</span>
                  <span className="mono font-medium text-fg-2">
                    {result.filledCount}/{result.buys.length}
                  </span>
                </div>
                <div className="truncate text-[11px] text-fg-3">{status.label}</div>
              </div>
            </Link>
          );
        })}
      </div>

      <div className="flex-1" />
      <div className="flex flex-col gap-1 border-t border-line p-3 text-[11px] text-fg-3">
        <div className="flex justify-between">
          <span>Kurs NBP</span>
          <span className="mono text-fg-2">{rateLabel}</span>
        </div>
        <div className="flex justify-between">
          <span>Odświeżono</span>
          <span className="mono text-fg-2">{refreshedLabel}</span>
        </div>
        <div className="flex justify-between">
          <span>Źródło</span>
          <span className="text-fg-2">CoinGecko · cache</span>
        </div>
      </div>
    </aside>
  );
}
